import React from "react";
import { Dropdown } from "react-bootstrap";
import { Socket } from "socket.io-client";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { useBackgroundTasks } from "../../../hooks/useBackgroundTasks";
import { IBackgroundTask } from "../../../contexts/BackgroundTasksContext";
import "./BackgroundTasksIndicator.scss";

type Props = {
    socket: Socket;
};

const getTaskIcon = (type: IBackgroundTask["type"]): string => {
    switch (type) {
        case "inventory_reset":
            return "fad fa-eraser";
        case "catalog_sync":
            return "fad fa-sync";
        case "export":
            return "fad fa-file-export";
        case "import":
            return "fad fa-file-import";
        default:
            return "fad fa-cog";
    }
};

const getStatusColor = (status: IBackgroundTask["status"]): string => {
    switch (status) {
        case "started":
        case "processing":
            return "var(--highlight-color)";
        case "completed":
            return "var(--success-color, #28a745)";
        case "error":
            return "var(--danger-color, #dc3545)";
        default:
            return "var(--main-text-color)";
    }
};

export const BackgroundTasksIndicator: React.FC<Props> = ({ socket }) => {
    const { tasks, activeTaskCount, clearCompletedTasks } = useBackgroundTasks(socket);

    // Don't render if no tasks
    if (tasks.length === 0) {
        return null;
    }

    const hasActiveTasks = activeTaskCount > 0;

    return (
        <Dropdown align="end" className="BackgroundTasksIndicator header-icon me-5">
            <Dropdown.Toggle as={CustomToggle} id="background-tasks-dropdown">
                <TaskIconWrapper $isActive={hasActiveTasks}>
                    <i className={hasActiveTasks ? "fad fa-spinner-third fa-spin" : "fad fa-tasks"} />
                    {activeTaskCount > 0 && <TaskBadge>{activeTaskCount}</TaskBadge>}
                </TaskIconWrapper>
                <div>
                    <FormattedMessage id="app.tasks" defaultMessage="Tasks" description="Tasks" />
                </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="task-dropdown-menu">
                <div className="task-dropdown-header">
                    <span>
                        <FormattedMessage id="background_tasks" defaultMessage="Background Tasks" />
                    </span>
                    {tasks.some((t) => t.status === "completed" || t.status === "error") && (
                        <button className="btn btn-sm btn-link p-0" onClick={clearCompletedTasks}>
                            <FormattedMessage id="clear" defaultMessage="Clear" />
                        </button>
                    )}
                </div>

                <Dropdown.Divider />

                {tasks.map((task) => (
                    <TaskItem key={task.id} task={task} />
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

// Custom toggle to match header icon styling
const CustomToggle = React.forwardRef<HTMLAnchorElement, { children: React.ReactNode; onClick: (e: React.MouseEvent) => void }>(
    ({ children, onClick }, ref) => (
        <a
            href="#"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="d-flex flex-column align-items-center text-center">
            {children}
        </a>
    )
);
CustomToggle.displayName = "CustomToggle";

// Individual task item component
const TaskItem: React.FC<{ task: IBackgroundTask }> = ({ task }) => {
    const progressPercent = Math.round(task.progress);
    const isActive = task.status === "started" || task.status === "processing";

    return (
        <TaskItemContainer>
            <div className="task-item-header">
                <span className="task-icon" style={{ color: getStatusColor(task.status) }}>
                    <i className={getTaskIcon(task.type)} />
                </span>
                <span className="task-name">{task.name}</span>
                <span className="task-status" style={{ color: getStatusColor(task.status) }}>
                    {task.status === "completed" && <i className="fas fa-check" />}
                    {task.status === "error" && <i className="fas fa-exclamation-triangle" />}
                    {isActive && <span>{progressPercent}%</span>}
                </span>
            </div>

            {isActive && (
                <div className="task-progress">
                    <ProgressBar $progress={progressPercent} />
                </div>
            )}

            {task.details && isActive && (
                <div className="task-details">
                    {task.details.processedItems !== undefined && task.details.totalItems !== undefined && (
                        <span>
                            {task.details.processedItems} / {task.details.totalItems} items
                        </span>
                    )}
                    {task.details.currentItem && <span className="current-item">({task.details.currentItem})</span>}
                </div>
            )}

            {task.errorMessage && <div className="task-error">{task.errorMessage}</div>}
        </TaskItemContainer>
    );
};

// Styled components
const TaskIconWrapper = styled.div<{ $isActive: boolean }>`
    position: relative;
    color: ${(props) => (props.$isActive ? "var(--highlight-color)" : "inherit")};
`;

const TaskBadge = styled.span`
    position: absolute;
    top: -8px;
    right: -10px;
    background-color: var(--highlight-color);
    color: var(--highlight-color-text, #fff);
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
`;

const TaskItemContainer = styled.div`
    padding: 0.5rem 1rem;

    &:not(:last-child) {
        border-bottom: 1px solid var(--minor-bg-color);
    }

    .task-item-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .task-icon {
        font-size: 1rem;
    }

    .task-name {
        flex: 1;
        font-weight: 500;
    }

    .task-status {
        font-size: 0.875rem;
    }

    .task-progress {
        margin-top: 0.5rem;
        height: 4px;
        background-color: var(--minor-bg-color);
        border-radius: 2px;
        overflow: hidden;
    }

    .task-details {
        margin-top: 0.25rem;
        font-size: 0.75rem;
        color: var(--main-text-color);
        opacity: 0.7;

        .current-item {
            margin-left: 0.25rem;
            font-style: italic;
        }
    }

    .task-error {
        margin-top: 0.25rem;
        font-size: 0.75rem;
        color: var(--danger-color, #dc3545);
    }
`;

const ProgressBar = styled.div<{ $progress: number }>`
    height: 100%;
    width: ${(props) => props.$progress}%;
    background-color: var(--highlight-color);
    transition: width 0.3s ease;
`;

export default BackgroundTasksIndicator;
